/**
 * KnowledgeArticlePage — Full-page read-only view for a knowledge article.
 *
 * Route: /knowledge/:articleId
 * Loads the article from the API first, then falls back to localStorage drafts.
 * Shows hero image, headline, byline, dateline, lede, body, tags, sources.
 * Back button returns to /knowledge.
 *
 * Pattern follows ChronicleViewPage: useParams → fetch → render.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { BLOG_TYPES } from '../../Data/Blog/blogTypes';
import { fetchArticle } from '../../api/blogArticles';
import type { BlogArticleDetail } from '../../api/blogArticles';
import type { BlogDraft } from '../../Data/Blog/blogDraftStore';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './KnowledgeArticlePage.module.css';

// ── localStorage reader (fallback for local drafts) ───────────

const STORAGE_KEY = 'l4e_blog_drafts';

function loadDraftById(draftId: string): BlogDraft | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw) as BlogDraft[];
    return all.find(d => d.draftId === draftId) ?? null;
  } catch {
    return null;
  }
}

// ── Helpers ──────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function estimateReadingTime(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

function resolveImageUrl(raw: string | undefined | null): string | null {
  return mediaSrc(raw);
}

/**
 * Normalize a BlogArticleDetail from the API into a shape compatible
 * with the existing render code (which was built for BlogDraft).
 */
function apiArticleToView(a: BlogArticleDetail) {
  return {
    source: 'api' as const,
    title: a.title,
    subtitle: a.subtitle ?? '',
    blogType: a.blogType,
    status: a.status,
    heroImageUrl: resolveImageUrl(a.featuredImageUrl),
    heroCaption: a.featuredImageAlt ?? '',
    heroCredit: '',
    headline: a.title,
    byline: a.authorDisplayName ?? '',
    dateline: '',
    lede: a.excerpt ?? '',
    abstract: '',
    bodyText: a.body ?? '',
    methodology: '',
    bibliography: '',
    acknowledgments: '',
    sources: a.citations?.map(c =>
      [c.sourceAuthor, c.sourceTitle, c.sourceUrl].filter(Boolean).join(' — ')
    ).join('\n') ?? '',
    tags: a.tags ?? [],
    keywords: [] as string[],
    wordCount: (a.body ?? '').trim().split(/\s+/).length,
    updatedAt: a.updatedAt ?? a.createdAt,
    articleGuid: a.articleGuid,
    draftId: '',
  };
}

function draftToView(d: BlogDraft) {
  return {
    source: 'draft' as const,
    title: d.headline || 'Untitled',
    subtitle: d.subtitle ?? '',
    blogType: d.blogType,
    status: d.status,
    heroImageUrl: d.heroImageUrl ?? null,
    heroCaption: d.heroCaption ?? '',
    heroCredit: d.heroCredit ?? '',
    headline: d.headline ?? '',
    byline: d.byline ?? '',
    dateline: d.dateline ?? '',
    lede: d.lede ?? '',
    abstract: d.abstract ?? '',
    bodyText: d.bodyText ?? '',
    methodology: d.methodology ?? '',
    bibliography: d.bibliography ?? '',
    acknowledgments: d.acknowledgments ?? '',
    sources: d.sources ?? '',
    tags: d.tags ?? [],
    keywords: d.keywords ?? [],
    wordCount: d.wordCount ?? 0,
    updatedAt: d.updatedAt,
    articleGuid: '',
    draftId: d.draftId,
  };
}

type ViewArticle = ReturnType<typeof apiArticleToView> | ReturnType<typeof draftToView>;

// ── Component ───────────────────────────────────────────────────

export default function KnowledgeArticlePage() {
  const { draftId: articleId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const { t } = useAppTranslation('pages');

  const [article, setArticle] = useState<ViewArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!articleId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Try API first (for published articles accessed from Impact page)
    fetchArticle(articleId)
      .then((data) => {
        if (!cancelled) {
          setArticle(apiArticleToView(data));
          setLoading(false);
        }
      })
      .catch(() => {
        // API failed — fall back to localStorage draft
        if (cancelled) return;
        const draft = loadDraftById(articleId);
        if (draft) {
          setArticle(draftToView(draft));
          setLoading(false);
        } else {
          setNotFound(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [articleId]);

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.notFound}>
          <p>{t('impact.previewLoading', 'Loading…')}</p>
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className={styles.root}>
        <div className={styles.notFound}>
          <h2>{t('knowledgeArticle.notFound')}</h2>
          <p>{t('knowledgeArticle.notFoundDesc')}</p>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/knowledge')}
          >
            {t('knowledgeArticle.backToKnowledge')}
          </button>
        </div>
      </div>
    );
  }

  const typeDef = BLOG_TYPES[article.blogType] || BLOG_TYPES.personal;
  const readMin = estimateReadingTime(article.bodyText);

  return (
    <div className={styles.root}>
      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate('/knowledge')}
        >
          ← {t('knowledgeArticle.backToKnowledge')}
        </button>
        <div className={styles.topBarRight}>
          <span className={styles.statusBadge}>
            {t(`knowledgeArticle.status.${article.status}`)}
          </span>
          {article.source === 'draft' && article.draftId && (
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => navigate('/knowledge', { state: { editDraftId: article.draftId } })}
            >
              {t('knowledgeArticle.editButton')}
            </button>
          )}
        </div>
      </div>

      {/* ── Hero image ── */}
      {article.heroImageUrl && (
        <div className={styles.heroWrap}>
          <img
            src={resolveImageUrl(article.heroImageUrl) ?? undefined}
            alt={article.heroCaption || ''}
            className={styles.heroImage}
          />
          {(article.heroCaption || article.heroCredit) && (
            <div className={styles.heroMeta}>
              {article.heroCaption && (
                <span className={styles.heroCaption}>{article.heroCaption}</span>
              )}
              {article.heroCredit && (
                <span className={styles.heroCredit}>{article.heroCredit}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Article content (bounding box below hero) ── */}
      <div className={styles.articleContent}>

      {/* ── Article header ── */}
      <div className={styles.articleHeader}>
        <div className={styles.typeBadge}>
          {typeDef.icon} {t(`knowledgeArticle.blogType.${article.blogType}`)}
        </div>
        <h1 className={styles.headline}>{article.headline || t('knowledgeArticle.untitled')}</h1>
        {article.subtitle && (
          <p className={styles.subtitle}>{article.subtitle}</p>
        )}
        <div className={styles.meta}>
          {article.byline && <span className={styles.byline}>{article.byline}</span>}
          {article.dateline && (
            <>
              <span className={styles.metaSep}>·</span>
              <span>{article.dateline}</span>
            </>
          )}
          <span className={styles.metaSep}>·</span>
          <span>{formatDate(article.updatedAt)}</span>
          <span className={styles.metaSep}>·</span>
          <span>{t('knowledgeArticle.readTime', { min: readMin })}</span>
        </div>
      </div>

      {/* ── Lede ── */}
      {article.lede && (
        <p className={styles.lede}>{article.lede}</p>
      )}

      {/* ── Abstract (scholarly) ── */}
      {article.abstract && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('knowledgeArticle.abstract')}</h2>
          <p className={styles.sectionBody}>{article.abstract}</p>
        </div>
      )}

      {/* ── Body ── */}
      {article.bodyText && (
        <div className={styles.body}>
          {article.bodyText.split('\n').map((para, i) =>
            para.trim() ? <p key={i} className={styles.paragraph}>{para}</p> : null
          )}
        </div>
      )}

      {/* ── Methodology (scholarly) ── */}
      {article.methodology && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('knowledgeArticle.methodology')}</h2>
          <p className={styles.sectionBody}>{article.methodology}</p>
        </div>
      )}

      {/* ── Bibliography (scholarly) ── */}
      {article.bibliography && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('knowledgeArticle.bibliography')}</h2>
          <p className={styles.sectionBody}>{article.bibliography}</p>
        </div>
      )}

      {/* ── Acknowledgments (scholarly) ── */}
      {article.acknowledgments && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('knowledgeArticle.acknowledgments')}</h2>
          <p className={styles.sectionBody}>{article.acknowledgments}</p>
        </div>
      )}

      {/* ── Sources ── */}
      {article.sources && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('knowledgeArticle.sources')}</h2>
          <p className={styles.sectionBody}>{article.sources}</p>
        </div>
      )}

      {/* ── Tags ── */}
      {article.tags.length > 0 && (
        <div className={styles.tagsRow}>
          {article.tags.map((tag, i) => (
            <span key={i} className={styles.tag}>{tag}</span>
          ))}
        </div>
      )}

      {/* ── Keywords (scholarly) ── */}
      {article.keywords && article.keywords.length > 0 && (
        <div className={styles.tagsRow}>
          <span className={styles.tagsLabel}>{t('knowledgeArticle.keywords')}:</span>
          {article.keywords.map((kw, i) => (
            <span key={i} className={styles.tag}>{kw}</span>
          ))}
        </div>
      )}

      </div>{/* end .articleContent */}
    </div>
  );
}
